import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import authApi from '@/api/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

const buildSchema = (t: (key: string) => string) =>
  z
    .object({
      password: z.string().min(8, t('validation:password_min')),
      password_confirmation: z.string(),
    })
    .refine((d) => d.password === d.password_confirmation, {
      message: t('validation:password_match'),
      path: ['password_confirmation'],
    });

type FormData = z.infer<ReturnType<typeof buildSchema>>;

export default function ResetPasswordPage() {
  const { t } = useTranslation('auth');
  const { t: tValidation } = useTranslation('validation');
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const email = searchParams.get('email') ?? '';
  const navigate = useNavigate();
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const tCombined = (key: string) =>
    key.startsWith('validation:') ? tValidation(key.slice('validation:'.length)) : t(key);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(buildSchema(tCombined)),
    defaultValues: { password: '', password_confirmation: '' },
  });

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    setApiError(null);
    try {
      await authApi.resetPassword({
        email,
        token,
        password: data.password,
        password_confirmation: data.password_confirmation,
      });
      navigate('/login', {
        replace: true,
        state: { resetMessage: t('reset_password.success_message') },
      });
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('reset_password.invalid_token');
      setApiError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg px-4">
      <Card className="w-full max-w-md rounded-[14px] border-border-c shadow-card-lift">
        <CardHeader className="space-y-2 text-center">
          <CardTitle className="font-brand text-[22px] font-extrabold tracking-[-0.015em] text-ink">
            {t('reset_password.title')}
          </CardTitle>
          <CardDescription className="text-[12.5px] text-muted-foreground">
            {t('reset_password.subtitle')}
          </CardDescription>
        </CardHeader>

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <CardContent className="space-y-4">
            {apiError && (
              <Alert variant="destructive">
                <AlertDescription>{apiError}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="reset-password">{t('reset_password.password_label')}</Label>
              <Input
                id="reset-password"
                type="password"
                autoComplete="new-password"
                placeholder={t('reset_password.password_placeholder')}
                aria-invalid={!!errors.password}
                disabled={isSubmitting}
                {...register('password')}
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="reset-password-confirmation">
                {t('reset_password.password_confirmation_label')}
              </Label>
              <Input
                id="reset-password-confirmation"
                type="password"
                autoComplete="new-password"
                aria-invalid={!!errors.password_confirmation}
                disabled={isSubmitting}
                {...register('password_confirmation')}
              />
              {errors.password_confirmation && (
                <p className="text-sm text-destructive">{errors.password_confirmation.message}</p>
              )}
            </div>
          </CardContent>

          <CardFooter className="flex flex-col gap-3">
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('reset_password.submitting')}
                </>
              ) : (
                t('reset_password.submit_button')
              )}
            </Button>
            <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground">
              {t('forgot_password.back_to_login')}
            </Link>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
